// pms-core / domain — Result + the domain error taxonomy.
//
// Driven ports (repositories, integrations) and driving ports (services) return
// a Result instead of throwing across the boundary: an adapter's transport
// failure (a Supabase error, a Channex 500) is data the application layer
// decides about (retry / dead-letter / surface to bos.*), not a stack-unwinding
// surprise. The DB RPCs still raise — the Supabase adapter is where a raised
// exception is caught and turned into an `err(...)`. Pure domain helpers that
// cannot fail (state-machine guards) return plain values, not Results.

export type PmsErrorKind =
  | 'not_found' // the aggregate/reference row does not exist
  | 'conflict' // optimistic-lock / idempotency clash / duplicate
  | 'invalid_transition' // state machine rejected the action (§5.2)
  | 'validation' // bad input (dates, occupancy, price)
  | 'forbidden' // RLS / role gate refused (§5.3)
  | 'unavailable' // no inventory (overbooking guard)
  | 'integration' // an external system (Channex, VAD, fiskaly, POS) failed
  | 'transient' // retryable transport failure — safe to re-enqueue
  | 'unknown';

export interface PmsError {
  kind: PmsErrorKind;
  /** Operator-facing French message where user-visible (repo R4). */
  message: string;
  /** True when a job runner should retry rather than dead-letter. */
  retryable: boolean;
  /** Adapter-specific detail for logs — never shown to staff. */
  cause?: unknown;
}

export type Ok<T> = { ok: true; value: T };
export type Err = { ok: false; error: PmsError };
export type Result<T> = Ok<T> | Err;

export const ok = <T>(value: T): Ok<T> => ({ ok: true, value });

export const err = (
  kind: PmsErrorKind,
  message: string,
  opts: { retryable?: boolean; cause?: unknown } = {},
): Err => ({
  ok: false,
  error: {
    kind,
    message,
    retryable: opts.retryable ?? kind === 'transient',
    cause: opts.cause,
  },
});

export const isOk = <T>(r: Result<T>): r is Ok<T> => r.ok;
export const isErr = <T>(r: Result<T>): r is Err => !r.ok;
